import { useCallback, useRef, useEffect } from "react";
import { callWithAuth, postListenTime } from "@/api/Api";
import { useUserSettings } from "@/hooks/useUserSettings";
import type { VideoActivity } from "../types";
import {
  playerRef,
  updateActiveWord,
  scrubbing$,
  isPaused$,
  seekPending$,
  seekRequestMs$,
  videoPlayed$,
  videoLoaded$,
  videoDuration$,
  scrubberSeekFraction$,
  addWatchTimeToMaxPosition,
  lessonDataCache$,
} from "../state/videoState";
import { incrementElapsedTime, playbackRate$ } from "../state/userState";

/** Client's local date in YYYY-MM-DD (matches iOS getLocalDate for listentime). */
function localDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface VideoPlayerOptions {
  lessonId?: number;
  chapterId?: number;
  /** When false, watching does NOT accrue listen-time toward the daily goal.
   *  Placement-test videos set this (iOS deliberately doesn't track placement
   *  watch time). Defaults to true (feed + lessons count normally). */
  countListenTime?: boolean;
}

// After a seek to the lesson start we wait for the player to actually land
// there. Treat the seek as settled once playback is within this window of the
// target, or bail after this many progress ticks so a never-landing seek can't
// stall word tracking forever.
const SEEK_SETTLE_WINDOW_MS = 3000;
const MAX_SEEK_PENDING_TICKS = 20;

export function useVideoPlayer(
  videoActivity: VideoActivity,
  options?: VideoPlayerOptions,
) {
  const lastSecondRef = useRef(-1);
  const seekPendingTicksRef = useRef(0);

  const { settings } = useUserSettings();
  // Read latest goal/level/context into a ref so the per-second tick (whose
  // identity is pinned) always sees current values without re-subscribing.
  const ctxRef = useRef({
    settings,
    lessonId: options?.lessonId,
    chapterId: options?.chapterId,
    countListenTime: options?.countListenTime,
  });
  ctxRef.current = {
    settings,
    lessonId: options?.lessonId,
    chapterId: options?.chapterId,
    countListenTime: options?.countListenTime,
  };

  const isPlaying = !isPaused$.get();
  const duration = videoDuration$.get();
  const playbackRate = playbackRate$.get();

  const youtubeId = videoActivity.youtubeId;
  const url = `https://www.youtube.com/watch?v=${youtubeId}`;

  const lessonStartMs = videoActivity.start;
  const lessonEndMs = videoActivity.end;
  const lessonDurationMs = lessonEndMs - lessonStartMs;
  const lessonDurationSec = lessonDurationMs / 1000;
  const lessonStartSec = lessonStartMs / 1000;

  const seekToFraction = useCallback((fraction: number): void => {
    playerRef.current?.seekTo(fraction, "fraction");
    videoPlayed$.set(fraction);
  }, []);

  // Pause when the tab goes to the background. Browsers keep an HLS/<video>
  // element playing in a hidden tab, so without this the per-second listen-time
  // tick keeps firing unattended and could farm the daily streak from a
  // backgrounded tab — impossible on iOS, where the app suspends in the
  // background. The progress tick is gated on `!isPaused$`, so pausing here
  // stops accrual; the user resumes with a tap on return (iOS parity).
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) isPaused$.set(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const handleProgress = useCallback(
    (state: { played: number; loaded: number; playedSeconds: number }) => {
      videoPlayed$.set(state.played);
      videoLoaded$.set(state.loaded);

      const currentTimeMs = state.playedSeconds * 1000;

      if (currentTimeMs >= lessonEndMs && duration > 0) {
        seekToFraction(lessonStartSec / duration);
        return;
      }

      if (seekPending$.peek()) {
        seekPendingTicksRef.current++;
        if (
          Math.abs(currentTimeMs - lessonStartMs) < SEEK_SETTLE_WINDOW_MS ||
          seekPendingTicksRef.current > MAX_SEEK_PENDING_TICKS
        ) {
          seekPending$.set(false);
          seekPendingTicksRef.current = 0;
        } else {
          return;
        }
      }

      // While the user is dragging the scrubber, the SCRUBBER drives the active
      // word from the finger position; don't let the still-playing video also
      // update it or the highlight ping-pongs between the finger and playback.
      if (!isPaused$.get() && !scrubbing$.peek()) {
        updateActiveWord(currentTimeMs);
      }

      const currentSecond = Math.floor(state.playedSeconds);
      // Only count listening time while actually playing. A scrubber/transcript
      // seek (or boundary loop) advances playedSeconds while paused; counting
      // those ticks inflates streak/listen-time progress without real playback.
      if (currentSecond !== lastSecondRef.current && !isPaused$.peek()) {
        lastSecondRef.current = currentSecond;

        // Placement-test videos don't count toward the daily listening goal
        // (iOS VideoStep wires no onWatchTimeUpdate). Skip both the local
        // elapsed-time accrual and the backend post for those.
        if (ctxRef.current.countListenTime === false) return;

        incrementElapsedTime();

        // Sync this second of listening time to the backend. From the feed we
        // also attach the lesson-interaction context iOS sends on every tick —
        // { chapterId, lessonNumber, userLevel } (FeedNew.tsx:1169-1179 →
        // trackListeningTime → postListenTime.trackLessonInteraction). lessonNumber
        // is the lesson's sequence (cached lesson data); userLevel is the user's
        // feed CEFR level. Context is omitted unless all three are present, matching
        // iOS (interactionTrackingContext is undefined otherwise).
        const { settings: s, lessonId, chapterId } = ctxRef.current;
        const lessonNumber =
          lessonId !== undefined
            ? lessonDataCache$[lessonId].peek()?.lesson?.sequence
            : undefined;
        const userLevel = s?.userFeedLevel ?? undefined;
        const trackLessonInteraction =
          chapterId !== undefined &&
          lessonNumber !== undefined &&
          userLevel !== undefined
            ? { chapterId, lessonNumber, userLevel }
            : undefined;

        // Advance the clip-relative max-reached position by this 1s of linear
        // watch time, so the 55%/90% activity triggers fire off real progress
        // and not YouTube's whole-video played fraction (iOS handleWatchTimeUpdate:
        // maxPositionReachedRef += seconds). Only while playing — a paused tick
        // shouldn't count as forward progress.
        if (lessonId !== undefined && !isPaused$.peek()) {
          addWatchTimeToMaxPosition(lessonId, 1000);
        }
        callWithAuth(postListenTime, {
          listenTime: 1,
          localDate: localDateString(),
          ...(lessonId !== undefined && { lessonId }),
          ...(trackLessonInteraction && { trackLessonInteraction }),
        }).catch(() => {
          /* fire-and-forget — non-critical */
        });
        // feed_listening_goal_reached fires from the streak flow
        // (features/streak/useFeedStreak.ts) at the server-side goal crossing
        // with iOS-parity properties — not from this local tick.
      }
    },
    [duration, lessonStartSec, lessonEndMs, lessonStartMs, seekToFraction],
  );

  const handleDuration = useCallback((d: number): void => {
    videoDuration$.set(d);
  }, []);

  const handleSeekFromScrubber = useCallback(
    (fraction: number): void => {
      // Duration is 0 during the window after a chapter swipe (new player not
      // yet measured). seekTo(absoluteSec / 0) === seekTo(Infinity); no-op until
      // we have a real duration.
      if (duration <= 0) return;
      const absoluteSec = lessonStartSec + fraction * lessonDurationSec;
      seekToFraction(absoluteSec / duration);
    },
    [lessonStartSec, lessonDurationSec, duration, seekToFraction],
  );

  useEffect(() => {
    if (duration > 0) {
      seekPendingTicksRef.current = 0;
      seekToFraction(lessonStartSec / duration);
    }
  }, [duration, lessonStartSec, seekToFraction]);

  const scrubberSeek = scrubberSeekFraction$.get();
  useEffect(() => {
    if (scrubberSeek !== null) {
      handleSeekFromScrubber(scrubberSeek);
      scrubberSeekFraction$.set(null);
    }
  }, [scrubberSeek, handleSeekFromScrubber]);

  // Tap-to-seek: when a transcript word is tapped it sets seekRequestMs$ to that
  // word's absolute start (ms in the full video); jump there. Without this reader
  // the tap marked the word but never moved the video.
  const seekRequestMs = seekRequestMs$.get();
  useEffect(() => {
    if (seekRequestMs !== null && duration > 0) {
      seekToFraction(seekRequestMs / 1000 / duration);
      seekRequestMs$.set(null);
    }
  }, [seekRequestMs, duration, seekToFraction]);

  return {
    url,
    playing: isPlaying,
    playbackRate,
    handleProgress,
    handleDuration,
    lessonStartMs,
    lessonEndMs,
  };
}
